import { log } from '@/log'
import { Events } from './event'

export type T = keyof Events

export type Subscriber<EventType extends T> = (e: Events[EventType]) => void

/**
 * イベントのタイプごとに用意されるオブザーバ。pub/subの機能を持つ。
 * 専門のライブラリに置き換えてもよいが、subscriber の例外を隔離する publish の契約を満たすこと（eventemitter3 等の一般的な EventEmitter は listener の例外を伝播させる）。
 * @see https://github.com/my-color/front/pull/6148#discussion_r811557575
 */
export interface IPublisher<EventType extends T> {
  /**
   * イベントタイプに対して定義されているイベント変数を処理するための、イベントハンドラをこのオブザーバに登録する。
   * @return 登録されたイベントハンドラを購読解除するための関数
   */
  addSubscriber: (subscriber: Subscriber<EventType>) => Unsubscriber
  /**
   * イベントタイプに対して定義されているイベント変数をこのオブザーバに送信する。
   * subscriber の例外は呼び出し元へ伝播せず、後続 subscriber の呼び出しも止めない
   * （pub 側が sub 側の処理内容に関心を持たずに済むようにするため。README の pub/sub 疎結合方針）。
   */
  publish: (event: Events[EventType]) => void
}

export class Publisher<EventType extends T> implements IPublisher<EventType> {
  private subscribers: Array<Subscriber<EventType>> = []

  addSubscriber(subscriber: Subscriber<EventType>): () => void {
    this.subscribers = [...this.subscribers, subscriber]

    /** subscribeで渡されたイベントハンドラを購読解除するための関数 */
    return () => {
      this.subscribers = this.subscribers.filter(l => l !== subscriber)
    }
  }

  /**
   * publish 直後にページ遷移する箇所で dataLayer への push が unload に負けないよう、同期で呼ぶ。
   */
  publish(event: Events[EventType]): void {
    this.subscribers.forEach((subscriber) => {
      try {
        subscriber(event)
      } catch (error) {
        log.error('A subscriber of EventBusForThirdParty threw an error.', error)
      }
    })
  }
}

type Unsubscriber = () => void

export default Publisher
